import { Suspense, use } from 'react'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { Link, useLoaderData } from 'react-router'
import { Flex } from '@radix-ui/themes'
import { PageHeading } from '@/components/subscription/page-heading'
import { Button } from '~/components/subscription/button'
import { TextLink } from '~/components/subscription/text-link'
import { ZendeskWidget } from '@/components/zendesk-widget'
import { InBulkCard } from './in-bulk-card'
import { AddInBulkCard } from './add-in-bulk-card'
import { formatDate, formatTime } from '@/lib/shipment'
import { findShippingStatus } from '@/lib/order'
import { createClient } from '@/api-client'
import { Me } from '@/api-client/Me'
import { Subscriptions } from '@/api-client/Subscriptions'
import { SubscriptionSchedules } from '@/api-client/SubscriptionSchedules'
import { Cards } from '@/api-client/Cards'
import { SubscriptionInventory, FlowerCollectionProduct } from '~/api-client/data-contracts'
import * as styles from './styles.css'
import calendar from './calendar.svg'
import box from './box.svg'

export const loader = async () => {
  const account = await fetchMe()
  const subscriptions = await fetchSubscriptions()
  const subscription = subscriptions.data.data[0]

  let placements = { data: { data: [] } } as Awaited<ReturnType<typeof fetchPlacements>>
  let address = { data: { data: { attributes: {} } } } as Awaited<ReturnType<typeof fetchAddress>>
  let schedules = { data: { data: [] } } as Awaited<ReturnType<typeof fetchSchedules>>
  let product = { data: { data: null } } as Awaited<ReturnType<typeof fetchProduct>>
  let inBulkPurchasables = { data: { data: [] } } as Awaited<ReturnType<typeof fetchInBulkPurchasables>>
  let orders = { data: { data: [] } } as Awaited<ReturnType<typeof fetchOrders>>
  if (subscription) {
    placements = await fetchPlacements(subscription.id)
    address = await fetchAddress(subscription.id)
    schedules = await fetchSchedules(address.data.data.attributes.postal_code)
    product = await fetchProduct(subscription.id)
    inBulkPurchasables = await fetchInBulkPurchasables()
    orders = await fetchOrders(subscription.id)
  }

  return [account, subscriptions, placements, address, schedules, product, inBulkPurchasables, orders] as const
}

function fetchMe() {
  return createClient(Me).getMe()
}

function fetchSubscriptions() {
  return createClient(Subscriptions).getSubscriptions()
}

function fetchPlacements(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdPlacements(id)
}

function fetchAddress(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdAddress(id)
}

function fetchSchedules(postalCode?: string) {
  return createClient(SubscriptionSchedules).getSubscriptionSchedules({ postal_code: postalCode, upcoming: '1', limit: '9' })
}

function fetchProduct(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdProduct(id)
}

function fetchInBulkPurchasables() {
  return createClient(Subscriptions).getSubscriptionsInBulkPurchasables({ category: 'lossless_bouquet' })
}

function fetchOrders(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdOrders(id, { include: ['order_items.shipping_statuses'] })
}

function fetchDefaultCard() {
  return createClient(Cards).getCardsDefault()
}

const creditCardPromise = fetchDefaultCard()
  .then((res) => res.data.data)
  .catch(() => null)

export const SubscriptionMypageTop: React.FC = () => {
  const [
    {
      data: { data: account },
    },
    {
      data: { data: subscriptions, included },
    },
    {
      data: { data: placements },
    },
    {
      data: { data: address },
    },
    {
      data: { data: schedules },
    },
    {
      data: { data: product },
    },
    {
      data: { data: inBulkPurchasables },
    },
    {
      data: { data: orders, included: ordersIncluded },
    },
  ] = useLoaderData<typeof loader>()
  const creditCard = use(creditCardPromise)

  const subscription = subscriptions[0]
  const orderableName = product?.attributes.name
  const inBulkProducts = included.filter((item) => {
    return item.type === 'flower_collection_product' && item.attributes.bulk_discounted_price > 0
  }) as unknown as FlowerCollectionProduct[]
  const date = subscription && new Date(subscription.attributes.next_deliver_at * 1000)
  const weekday = date ? format(date, 'E', { locale: ja }) : ''
  const currentSchedules =
    subscription?.relationships.inventories.data
      .map((inventory: SubscriptionInventory) => schedules.find((s) => s.relationships.inventory.data.id === inventory.id))
      .sort((a, b) => a.attributes.deliver_at[0] - b.attributes.deliver_at[0]) || []
  const latestShipping = findShippingStatus(orders[0], ordersIncluded)
  const latestShippingStatus = latestShipping?.attributes?.status

  return (
    <Flex direction="column" align="center" gap="32px">
      <PageHeading>マイページ</PageHeading>
      {subscription ? (
        <>
          <p className={styles.note}>{formatTime(1000 * subscription.attributes.skip_deadline)}まで今後のお届け分のスキップ・配送先などの変更が可能です</p>
          {['not_shipped', 'preparation', 'transit'].includes(latestShippingStatus) && (
            <Flex direction="column" align="start" gap="8px" className={styles.card}>
              <h3 className={styles.cardHeading}>次回の到着日</h3>
              <Flex direction="row" align="center" gap="4px">
                <p className={styles.date}>{formatDate(1000 * latestShipping.attributes.deliver_at, 'M/d（E）')}</p>
                <span className={styles.statusLabel}>{latestShipping.attributes.status_label}</span>
              </Flex>
              {latestShipping.attributes.tracking_url && (
                <Button as="a" href={latestShipping.attributes.tracking_url} size="s">
                  追跡する
                </Button>
              )}
            </Flex>
          )}
          <Flex direction="column" align="start" gap="8px" className={styles.card}>
            <h3 className={styles.cardHeading}>今後のお届け予定</h3>
            <Flex direction="column" align="start" gap="4px">
              {currentSchedules.map((schedule, index) => {
                const date = formatDate(schedule?.attributes.deliver_at[0] * 1000, 'M/d（E）')
                const status = placements[0]?.attributes?.status

                return (
                  <Flex key={schedule.id} direction="row" align="center" gap="8px">
                    {index === 0 ? (
                      <>
                        <span className={styles.nextDate} style={{ textDecoration: status === 'skipped' ? 'line-through' : 'none' }}>
                          {date}
                        </span>
                        {status === 'skipped' && (
                          <Link to="/subscription/mypage/skip" className={styles.boldLink}>
                            スキップを取り消す
                          </Link>
                        )}
                        {status === 'pending' && (
                          <Link to="/subscription/mypage/skip" className={styles.boldLink}>
                            お届けをスキップ
                          </Link>
                        )}
                      </>
                    ) : (
                      <span className={styles.date}>{date}</span>
                    )}
                  </Flex>
                )
              })}
            </Flex>
            <Flex direction="column" align="start" gap="3px">
              <Flex direction="row" align="center" gap="4px" className={styles.note}>
                <img src={calendar} className={styles.noteIcon} />
                月に{currentSchedules.length}回・{weekday}曜日にお届け
              </Flex>
              <Flex direction="row" align="center" gap="4px" className={styles.note}>
                <img src={box} className={styles.noteIcon} />
                クロネコヤマトで配送（対面でのお受け取り）
              </Flex>
            </Flex>
            <Link className={styles.boldLink} to="/subscription/mypage/schedule">
              受け取りスケジュールを変更
            </Link>
          </Flex>
        </>
      ) : (
        <Flex direction="column" align="start" gap="8px" className={styles.card}>
          <p className={styles.text}>ご契約中の定期便はありません</p>
          <Button as="a" href="/subscription/new" size="s">
            定期便を申し込む
          </Button>
        </Flex>
      )}
      <InBulkCard subscription={subscription} product={product} inBulkProducts={inBulkProducts} placements={placements} />
      {inBulkProducts.length === 0 && <AddInBulkCard subscription={subscription} inBulkProducts={inBulkPurchasables} />}
      {subscription && (
        <Flex direction="column" align="start" gap="8px" className={styles.card}>
          <h3 className={styles.cardHeading}>お届け先</h3>
          <Flex direction="column" align="start" gap="12px">
            {address?.attributes ? (
              <Flex direction="column" gap="2px" className={styles.text}>
                <span>〒{address.attributes.postal_code}</span>
                <span>
                  {address.attributes.state}
                  {address.attributes.city_and_street}
                </span>
                <span>{address.attributes.others}</span>
                <span>
                  {address.attributes.name}
                  {!!address.attributes.sender_name ? `（${address.attributes.sender_name}より）` : ''}
                </span>
              </Flex>
            ) : (
              <p className={styles.warn}>登録されていません</p>
            )}
            <Button as="a" href="/subscription/mypage/address" size="s">
              お届け先を変更する
            </Button>
          </Flex>
        </Flex>
      )}
      <Flex direction="column" align="start" gap="8px" className={styles.card}>
        <h3 className={styles.cardHeading}>メールアドレス</h3>
        <Flex direction="column" align="start" gap="12px">
          <Flex direction="column" gap="2px" className={styles.text}>
            <span>{account.attributes.email}</span>
          </Flex>
          <Button as="a" href="/subscription/mypage/email" size="s">
            メールアドレスを変更する
          </Button>
          <Button as="a" href="/subscription/mypage/password" size="s">
            パスワードを変更する
          </Button>
        </Flex>
      </Flex>
      <Flex direction="column" align="start" gap="8px" className={styles.card}>
        <h3 className={styles.cardHeading}>クレジットカード</h3>
        <Flex direction="column" align="start" gap="12px">
          <Suspense fallback={<></>}>
            {creditCard ? <p className={styles.text}>**** **** **** {creditCard.attributes?.last4}</p> : <p className={styles.warn}>登録されていません</p>}
          </Suspense>
          <Button as="a" href="/subscription/mypage/card" size="s">
            クレジットカードを変更する
          </Button>
        </Flex>
      </Flex>
      <Flex direction="column" align="center" gap="24px">
        {subscription && <TextLink to="/subscription/mypage/cancel">定期便の解約</TextLink>}
        <TextLink to="/subscription/mypage/delete">アカウントの削除</TextLink>
      </Flex>
      <ZendeskWidget orderableName={orderableName} userId={account?.id} userEmail={account?.attributes.email} />
    </Flex>
  )
}
